
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIsTabletOrMobile } from "@/hooks/use-tablet-mobile";
import { useIsMobile } from "@/hooks/use-mobile";
import { devLogger } from "@/lib/dev-logger";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isTabletOrMobile = useIsTabletOrMobile();
  const isMobile = useIsMobile();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Password reset logic will go here
    devLogger.log("Password reset request for:", email);
    setIsSubmitted(true);
  };

  const FooterContent = () => (
    <div className="w-full">
      <div className="flex justify-between items-center text-sm text-gray-400 mb-4">
        <div className="flex space-x-2">
          <Link to="/terms" className="hover:text-gray-600 transition-colors">
            Terms of Use
          </Link>
          <Link to="/privacy" className="hover:text-gray-600 transition-colors">
            Privacy Policy
          </Link>
        </div>
        <p className="text-xs">© 2025 Studibudi</p>
      </div>
    </div>
  );

  if (isSubmitted) {
    if (isTabletOrMobile) {
      return (
        <div className="min-h-screen flex flex-col bg-app-background">
          {/* Top Navigation */}
          <nav className="w-full px-4 h-[58px] flex items-center">
            <div className="flex justify-start">
              <img src="/lovable-uploads/72a43dd6-4d4e-41ee-a66d-387b3974ffb8.png" alt="Studibudi Logo" className="h-6 w-auto" />
            </div>
          </nav>
          
          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center px-4">
            <div className="w-full max-w-md">
              <div style={!isMobile ? {
                backdropFilter: 'blur(40px)'
              } : {
                backdropFilter: 'blur(40px)'
              }} className="backdrop-blur-3xl rounded-2xl">
                {/* Success Message */}
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="text-xl font-medium text-gray-900 mb-6">Check your email</h2>
                    <p className="text-gray-600 mb-8">
                      We've sent a password reset link to {email}
                    </p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <Button
                      onClick={() => setIsSubmitted(false)}
                      variant="outline"
                      className="w-full h-12 border border-gray-200 hover:bg-gray-50 bg-white rounded-2xl shadow-none"
                    >
                      Try another email
                    </Button>

                    <div className="text-center">
                      <Link 
                        to="/login" 
                        className="text-blue-600 hover:text-blue-700 transition-colors text-sm"
                      >
                        Back to login
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer at bottom */}
          <footer className="w-full px-4 pb-4">
            <FooterContent />
          </footer>
        </div>
      );
    }

    // Desktop layout
    return (
      <div className="min-h-screen flex flex-col bg-app-background">
        {/* Top Navigation */}
        <nav className="w-full px-4 h-[58px] flex items-center">
          <div className="flex justify-start">
            <img src="/lovable-uploads/72a43dd6-4d4e-41ee-a66d-387b3974ffb8.png" alt="Studibudi Logo" className="h-6 w-auto" />
          </div>
        </nav>
        
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center px-[80px]">
          <div className="flex-1 flex items-center justify-center">
            <div style={{
              backdropFilter: 'blur(40px)'
            }} className="w-full backdrop-blur-3xl max-w-md rounded-2xl">
              {/* Success Message */}
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-medium text-gray-900 mb-6">Check your email</h2>
                  <p className="text-gray-600 mb-8">
                    We've sent a password reset link to {email}
                  </p>
                </div>

                <div className="space-y-3 mb-3">
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="w-full h-12 border border-gray-200 hover:bg-gray-50 bg-white rounded-2xl shadow-none"
                  >
                    Try another email
                  </Button>

                  <div className="text-center">
                    <Link 
                      to="/login" 
                      className="text-blue-600 hover:text-blue-700 transition-colors text-sm"
                    >
                      Back to login
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer at bottom */}
        <footer className="w-full px-4 pb-4">
          <FooterContent />
        </footer>
      </div>
    );
  }

  if (isTabletOrMobile) {
    return (
      <div className="min-h-screen flex flex-col bg-app-background">
        {/* Top Navigation */}
        <nav className="w-full px-4 h-[58px] flex items-center">
          <div className="flex justify-start">
            <img src="/lovable-uploads/72a43dd6-4d4e-41ee-a66d-387b3974ffb8.png" alt="Studibudi Logo" className="h-6 w-auto" />
          </div>
        </nav>
        
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <div style={!isMobile ? {
              backdropFilter: 'blur(40px)'
            } : {
              backdropFilter: 'blur(40px)'
            }} className="backdrop-blur-3xl rounded-2xl">
              {/* Forgot Password Form */}
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-medium text-gray-900 mb-6">Forgot your password?</h2>
                  <p className="text-gray-600 mb-8">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-10 px-4 bg-gray-100 border border-gray-300 rounded-2xl text-gray-800 placeholder:text-gray-400 placeholder:font-light focus:outline-none focus:ring-0"
                      required
                    />
                  </div>

                  <div className="pt-3">
                    <Button
                      type="submit"
                      className="w-full h-12 bg-black hover:bg-gray-800 text-white font-medium rounded-2xl border-0 shadow-none"
                    >
                      Send reset link
                    </Button>
                  </div>
                </form>

                {/* Links */}
                <div className="flex justify-between text-sm mt-6 mb-6">
                  <Link 
                    to="/login" 
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    Back to login
                  </Link>
                  <Link 
                    to="/signup" 
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    Sign up
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer at bottom */}
        <footer className="w-full px-4 pb-4">
          <FooterContent />
        </footer>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="min-h-screen flex flex-col bg-app-background">
      {/* Top Navigation */}
      <nav className="w-full px-4 h-[58px] flex items-center">
        <div className="flex justify-start">
          <img src="/lovable-uploads/72a43dd6-4d4e-41ee-a66d-387b3974ffb8.png" alt="Studibudi Logo" className="h-6 w-auto" />
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-[80px]">
        <div className="flex-1 flex items-center justify-center">
          <div style={{
            backdropFilter: 'blur(40px)'
          }} className="w-full backdrop-blur-3xl max-w-md rounded-2xl">
            {/* Forgot Password Form */}
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-medium text-gray-900 mb-6">Forgot your password?</h2>
                <p className="text-gray-600 mb-8">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-10 px-4 bg-gray-100 border border-gray-300 rounded-2xl text-gray-800 placeholder:text-gray-400 placeholder:font-light focus:outline-none focus:ring-0"
                    required
                  />
                </div>

                <div className="pt-3">
                  <Button
                    type="submit"
                    className="w-full h-12 bg-black hover:bg-gray-800 text-white font-medium rounded-2xl border-0 shadow-none"
                  >
                    Send reset link
                  </Button>
                </div>
              </form>

              {/* Links */}
              <div className="flex justify-between text-sm mt-6 mb-3">
                <Link 
                  to="/login" 
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Back to login
                </Link>
                <Link 
                  to="/signup" 
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Sign up
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer at bottom */}
      <footer className="w-full px-4 pb-4">
        <FooterContent />
      </footer>
    </div>
  );
};

export default ForgotPasswordPage;
